import type { AppState } from '../types';
import { api } from './api-client';

export type FunnelEventName =
  | 'visitor'
  | 'started'
  | 'personal_details_completed'
  | 'focus_completed'
  | 'free_reading_viewed'
  | 'offer_viewed'
  | 'package_selected'
  | 'checkout_started'
  | 'payment_verified'
  | 'paid_reading_viewed'
  | 'funnel_started'
  | 'q1_viewed'
  | 'q2_viewed'
  | 'q3_viewed'
  | 'q4_viewed'
  | 'free_result_viewed'
  | 'paywall_viewed'
  | 'paid_result_viewed'
  | 'pdf_saved'
  | 'validation_error';
export type FunnelStepId = 'visitor' | 'started' | 'personal_details' | 'focus' | 'free_reading' | 'offer' | 'package' | 'checkout' | 'payment' | 'paid_reading' | 'landing' | 'q1' | 'q2' | 'q3' | 'q4' | 'free_result' | 'paywall' | 'purchase' | 'paid_result';
export type CtaPlacement = 'primary' | 'lower' | 'sticky';
const JOURNEY_STORAGE_KEY = 'arcana_funnel_journey_v1';
const JOURNEY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
interface StoredJourney { journey_id:string;journey_token:string;visitor_id:string;created_at:number }

export function coarseDevice(width = window.innerWidth): 'mobile' | 'tablet' | 'desktop' {
  if (width <= 600) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}

export function coarseBrowser(userAgent = navigator.userAgent): 'Safari' | 'Chrome' | 'Firefox' | 'Edge' | 'Other' {
  if (/Edg\//.test(userAgent)) return 'Edge';
  if (/(?:Chrome|CriOS)\//.test(userAgent)) return 'Chrome';
  if (/(?:Firefox|FxiOS)\//.test(userAgent)) return 'Firefox';
  if (/Safari\//.test(userAgent) && !/(?:Chrome|CriOS|Chromium|Edg|OPR)\//.test(userAgent)) return 'Safari';
  return 'Other';
}

let sequence: Promise<unknown> = Promise.resolve();
function enqueue(task: () => Promise<unknown>): void { sequence = sequence.then(task, task).catch(() => undefined); }

function opaqueBrowserToken():string{const bytes=crypto.getRandomValues(new Uint8Array(32));return btoa(String.fromCharCode(...bytes)).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'')}
function journeyCredential(visitorId:string):StoredJourney{
  try{const parsed=JSON.parse(sessionStorage.getItem(JOURNEY_STORAGE_KEY)??'null') as Partial<StoredJourney>|null;if(parsed&&parsed.visitor_id===visitorId&&typeof parsed.journey_id==='string'&&/^[0-9a-f-]{36}$/i.test(parsed.journey_id)&&typeof parsed.journey_token==='string'&&parsed.journey_token.length>=30&&typeof parsed.created_at==='number'&&Date.now()-parsed.created_at<JOURNEY_MAX_AGE_MS)return parsed as StoredJourney;}catch{/* Invalid session data is replaced below. */}
  const created={journey_id:crypto.randomUUID(),journey_token:opaqueBrowserToken(),visitor_id:visitorId,created_at:Date.now()};try{sessionStorage.setItem(JOURNEY_STORAGE_KEY,JSON.stringify(created));}catch{/* Telemetry persistence is best-effort. */}return created;
}
export function clearFirstPartyJourney():void{try{sessionStorage.removeItem(JOURNEY_STORAGE_KEY);}catch{/* Telemetry cleanup is best-effort. */}}

export async function beginFirstPartyJourney(state: AppState): Promise<Pick<AppState, 'journey_id' | 'journey_token' | 'returning_visitor'>> {
  const credential=journeyCredential(state.visitor_id);
  return api('begin-funnel-journey', {
    journey_id:credential.journey_id,journey_token:credential.journey_token,
    visitor_id: state.visitor_id, entry_point: 'organic', funnel_version: state.funnel_version,
    device_class: coarseDevice(), browser_family: coarseBrowser(),
    source: state.source, medium: state.medium, campaign: state.campaign,
  });
}

export function recordFirstPartyEvent(
  state: AppState,
  eventName: FunnelEventName,
  details: {
    step_id?: FunnelStepId;
    error_category?: string;
    package_id?: string;
    package_months?: number;
    package_price_cents?: number;
    funnel_version?: string;
  } = {},
): void {
  if (!state.resume_token && !state.journey_token) return;
  const body = {
    ...(state.resume_token ? {} : { journey_token: state.journey_token }),
    event_name: eventName,
    client_event_id: crypto.randomUUID(),
    ...(state.funnel_version ? { funnel_version: state.funnel_version } : {}),
    ...(details.package_id ? { package_id: details.package_id } : {}),
    ...(details.package_months !== undefined ? { package_months: details.package_months } : {}),
    ...(details.package_price_cents !== undefined ? { package_price_cents: details.package_price_cents } : {}),
    ...details,
  };
  enqueue(() => api('record-funnel-event', body, state.resume_token));
}
